import {
  apiFetch,
  DuplicateCandidatesError,
  NotFoundError,
  RateLimitedError,
  SpamDetectedError,
  ValidationError,
} from "../api";

import {
  type BodyByEndpoint,
  type Endpoint,
  getDb,
  type OutboxItem,
  type ResponseByEndpoint,
} from "./db";

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const REPORTS_PER_FLUSH = 10;
const MAX_TRIES = 5;
const FLUSH_TIMEOUT_MS = 30_000;
const MAX_FLUSH_PASSES = 10;

export type QueueListener = (event: QueueEvent) => void;

export type QueueEvent =
  | { kind: "queued"; pending: number }
  | { kind: "flushing" }
  | { kind: "flushed"; succeeded: number; failed: number }
  | { kind: "report-stale-discarded"; count: number }
  | { kind: "reports-cap-discarded"; count: number }
  | { kind: "station-missing-discarded"; count: number; stationId: string }
  | {
      kind: "permanent-failure";
      item: OutboxItem;
      reason: "duplicate" | "validation" | "spam" | "max-tries";
    }
  | { kind: "flush-timeout" }
  | { kind: "rate-limited"; retryAfter: number };

const listeners = new Set<QueueListener>();
let flushInFlight: Promise<void> | null = null;

export function subscribe(fn: QueueListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(event: QueueEvent): void {
  for (const fn of listeners) fn(event);
}

async function emitPending(): Promise<void> {
  emit({ kind: "queued", pending: await getDb().outbox.count() });
}

// IDB delete is best-effort: a failed delete after a successful dispatch
// would otherwise be misclassified as a dispatch failure and re-enqueued
// on the next flush, producing duplicates server-side. Log + swallow.
async function safeDelete(id: number): Promise<void> {
  try {
    await getDb().outbox.delete(id);
  } catch (err) {
    console.warn("[offlineQueue] outbox.delete failed", { id, error: String(err) });
  }
}

async function safeBulkDelete(ids: number[]): Promise<void> {
  if (ids.length === 0) return;
  try {
    await getDb().outbox.bulkDelete(ids);
  } catch (err) {
    console.warn("[offlineQueue] outbox.bulkDelete failed", {
      count: ids.length,
      error: String(err),
    });
  }
}

// Generic over Endpoint so the call site is statically typed: passing
// `{endpoint: "POST /stations", body: ...}` enforces `body: StationCreate`,
// no Record<string, unknown> cast required. Resolves to the dispatch
// response when the item ran synchronously; undefined when persisted offline.
export async function enqueue<E extends Endpoint>(write: {
  endpoint: E;
  body: BodyByEndpoint[E];
}): Promise<ResponseByEndpoint[E] | undefined> {
  const db = getDb();
  const id = await db.outbox.add({
    endpoint: write.endpoint,
    body: write.body as unknown as Record<string, unknown>,
    createdAt: Date.now(),
    tries: 0,
  });
  await emitPending();

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return undefined;
  }

  // Online: dispatch this exact item synchronously so the caller sees the
  // typed error (RateLimitedError, ValidationError, etc.) and the response.
  const item = await db.outbox.get(id);
  if (!item) return undefined;
  try {
    const response = await dispatch(item);
    await safeDelete(id);
    await emitPending();
    return response as ResponseByEndpoint[E];
  } catch (err) {
    if (
      err instanceof ValidationError ||
      err instanceof SpamDetectedError ||
      err instanceof DuplicateCandidatesError
    ) {
      // Permanent: drop so it can't re-fire on a later flush.
      await safeDelete(id);
      await emitPending();
    }
    // Rate-limit / network / unauthorized / 5xx: keep in queue; later flush
    // will retry. Caller renders a banner.
    throw err;
  }
}

export async function pendingCount(): Promise<number> {
  return getDb().outbox.count();
}

// Synchronous (non-async) so the in-flight Promise identity is preserved
// across concurrent callers — `async` would wrap each return in a fresh
// Promise and break the mutex contract.
export function flush(): Promise<void> {
  if (flushInFlight) return flushInFlight;
  flushInFlight = withTimeout(doFlushUntilEmpty(), FLUSH_TIMEOUT_MS).finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      emit({ kind: "flush-timeout" });
      reject(new Error("flush timed out"));
    }, ms);
  });
  try {
    return await Promise.race([p, timeout]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

// Run flush passes until the outbox is empty (caps recursion). Items
// enqueued mid-pass are picked up on the next iteration so they aren't
// stranded until the next online/offline transition.
async function doFlushUntilEmpty(): Promise<void> {
  let totalSucceeded = 0;
  let totalFailed = 0;
  let stopped = false;

  for (let pass = 0; pass < MAX_FLUSH_PASSES; pass += 1) {
    const result = await doFlushPass();
    totalSucceeded += result.succeeded;
    totalFailed += result.failed;
    if (result.stopped) {
      stopped = true;
      break;
    }
    if (result.processed === 0) break;
  }

  if (!stopped) {
    emit({ kind: "flushed", succeeded: totalSucceeded, failed: totalFailed });
  }
}

type PassResult = { succeeded: number; failed: number; processed: number; stopped: boolean };

async function doFlushPass(): Promise<PassResult> {
  emit({ kind: "flushing" });
  const db = getDb();
  let items = await db.outbox.orderBy("createdAt").toArray();
  if (items.length === 0) {
    return { succeeded: 0, failed: 0, processed: 0, stopped: false };
  }

  // Discard stale POST /reports (would expire server-side on insert).
  const now = Date.now();
  const staleReports = items.filter(
    (i) => i.endpoint === "POST /reports" && now - i.createdAt > TWO_HOURS_MS,
  );
  if (staleReports.length > 0) {
    await safeBulkDelete(staleReports.map((i) => i.id!));
    emit({ kind: "report-stale-discarded", count: staleReports.length });
    items = items.filter((i) => !staleReports.includes(i));
  }

  // Cap at 10 reports per flush; drop the OLDEST beyond the cap so the
  // most recent observations win (per Plan 3 spec).
  const reportItems = items.filter((i) => i.endpoint === "POST /reports");
  if (reportItems.length > REPORTS_PER_FLUSH) {
    const overflow = reportItems.slice(0, reportItems.length - REPORTS_PER_FLUSH);
    await safeBulkDelete(overflow.map((i) => i.id!));
    emit({ kind: "reports-cap-discarded", count: overflow.length });
    items = items.filter((i) => !overflow.includes(i));
  }

  let succeeded = 0;
  let failed = 0;
  let stopped = false;
  const stationToasted = new Set<string>();

  for (const item of items) {
    let dispatched = false;
    try {
      await dispatch(item);
      dispatched = true;
    } catch (err) {
      if (err instanceof RateLimitedError) {
        emit({ kind: "rate-limited", retryAfter: err.retryAfter });
        emit({ kind: "flushed", succeeded, failed });
        stopped = true;
        break;
      }
      if (err instanceof NotFoundError && item.endpoint === "POST /reports") {
        const stationId = String(item.body.station_id ?? "");
        if (stationId && !stationToasted.has(stationId)) {
          stationToasted.add(stationId);
          const matches = items.filter(
            (i) => i.endpoint === "POST /reports" && i.body.station_id === stationId,
          );
          await safeBulkDelete(matches.map((i) => i.id!));
          emit({ kind: "station-missing-discarded", count: matches.length, stationId });
        }
        continue;
      }
      if (
        err instanceof ValidationError ||
        err instanceof SpamDetectedError ||
        err instanceof DuplicateCandidatesError
      ) {
        await safeDelete(item.id!);
        emit({ kind: "permanent-failure", item, reason: reasonFor(err) });
        failed += 1;
        continue;
      }
      // Generic: increment tries; drop permanently after MAX_TRIES.
      const nextTries = item.tries + 1;
      if (nextTries >= MAX_TRIES) {
        await safeDelete(item.id!);
        emit({ kind: "permanent-failure", item, reason: "max-tries" });
        failed += 1;
      } else {
        try {
          await db.outbox.update(item.id!, { tries: nextTries });
        } catch (updateErr) {
          console.warn("[offlineQueue] outbox.update failed", {
            id: item.id,
            error: String(updateErr),
          });
        }
      }
    }
    if (dispatched) {
      await safeDelete(item.id!);
      succeeded += 1;
    }
  }

  await emitPending();

  return { succeeded, failed, processed: items.length, stopped };
}

function reasonFor(err: unknown): "duplicate" | "validation" | "spam" {
  if (err instanceof DuplicateCandidatesError) return "duplicate";
  if (err instanceof SpamDetectedError) return "spam";
  return "validation";
}

// Endpoints that require an authenticated user — backend gates them on
// `require_user`. The queue must attach the access token via apiFetch's
// `auth: true` flag, otherwise requests come in anonymously and are 401'd.
// The reports + stations endpoints accept anon callers and don't need this.
const AUTH_REQUIRED_ENDPOINTS = new Set<Endpoint>(["POST /favorites", "DELETE /favorites"]);

async function dispatch(item: OutboxItem): Promise<unknown> {
  const [method, path] = item.endpoint.split(" ", 2) as ["POST" | "DELETE", string];
  const url = resolveUrl(path, item.body);
  return await apiFetch(url, {
    method,
    body: extractBody(item.endpoint, item.body),
    auth: AUTH_REQUIRED_ENDPOINTS.has(item.endpoint),
  });
}

function resolveUrl(path: string, body: Record<string, unknown>): string {
  if (path === "/favorites" || path.startsWith("/favorites/")) {
    const stationId = body.station_id;
    if (typeof stationId === "string") return `/favorites/${stationId}`;
  }
  return path;
}

function extractBody(
  endpoint: Endpoint,
  body: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (endpoint === "POST /favorites" || endpoint === "DELETE /favorites") {
    return undefined;
  }
  return body;
}
